import { p, tp } from '../../i18n/useP';
/* Modèle Liquidités / BFRE — calcul pur, sans React.
   Source unique partagée par l'écran (FinancialsWorkingCapitalTab) et
   l'impression (LiquidityRatiosPrint, BfrePrint). Toute la logique de calcul
   (3 degrés de liquidité, BFRE 12 mois roulants, rotations DSO/DIO/DPO) vit ici
   pour éviter la duplication / dérive entre l'écran et le print. */

export const M3 = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'].map((mo) => p(mo));
export const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
export const marr = (o) => Array.from({ length: 12 }, (_, m) => num(o?.[String(m + 1)]));
export const findCode = (rows, code) => (rows || []).find((r) => r.code === code) || null;
const LIAB = new Set(['current_liabilities', 'long_term_liabilities']);

/* bs/pl sur 3 ans (N, N-1, N-2) requis : la moyenne 12 mois des rotations exige
   l'historique. Renvoie null si le bilan de l'année courante manque. */
/* `horizon` — nombre de mois calculés. Par défaut les mois clôturés (vue au
   réel) ; 12 pour la vue prévisionnelle, alimentée par le bilan hybride. Le
   CALCUL est le même : un seul jeu de formules DSO/DIO/DPO pour les deux vues,
   elles ne peuvent donc pas diverger. */
export function buildWorkingCapitalModel({ bs, bsPrev, bsPrev2, pl, plPrev, plPrev2, year, activeTenant, horizon }) {
  if (!bs) return null;
  const rows = (bs.rows || []).filter((r) => r.type === 'detail');
  const closed = Number.isFinite(horizon)
    ? horizon
    : (Number.isFinite(bs.closed_count) ? bs.closed_count : 12);
  const catOf = {};
  const bfreOf = {};
  const stdKeyOf = {};
  (bs.lines || []).forEach((l) => { catOf[l.id] = l.cf_category; bfreOf[l.id] = l.is_bfre; stdKeyOf[l.id] = l.std_key; });
  const rowByStd = (key) => rows.find((r) => stdKeyOf[r.code] === key) || null;
  const hasPrev = !!bsPrev;
  const hasPrev2 = !!bsPrev2;
  const prevByCode = {};
  (bsPrev?.rows || []).filter((r) => r.type === 'detail').forEach((r) => { prevByCode[r.code] = r; });
  const prev2ByCode = {};
  (bsPrev2?.rows || []).filter((r) => r.type === 'detail').forEach((r) => { prev2ByCode[r.code] = r; });
  const prevDec = (code) => num(prevByCode[code]?.months?.['12']);

  // ── BFRE (table) ──────────────────────────────────────────────────────
  const cashRows = rows.filter((r) => catOf[r.code] === 'cash');
  const bfreRows = rows.filter((r) => bfreOf[r.code]);
  const hasBfre = bfreRows.length > 0;
  const sumRows = (rs, src, m) => rs.reduce((a, r) => a + num((src[r.code] || r).months?.[String(m + 1)]), 0);

  const treso = Array.from({ length: 12 }, (_, i) => sumRows(cashRows, {}, i));
  const tresoOpen = cashRows.reduce((a, r) => a + prevDec(r.code), 0);
  const mkComp = (r, sign) => ({
    label: r.label,
    vals: marr(r.months).map((x) => sign * x),
    open: sign * prevDec(r.code),
  });
  // componentsAll = toutes les lignes is_bfre (ordre structure, aligné sur l'onglet
  // BFRE dédié) ; components = filtrées des lignes nulles (vue compacte).
  const componentsAll = bfreRows.map((r) => mkComp(r, LIAB.has(r.section) ? -1 : 1));
  const components = componentsAll.filter((c) => c.vals.slice(0, closed).some((v) => Math.round(v) !== 0) || Math.round(c.open) !== 0);
  const bfreSign = (r) => (LIAB.has(r.section) ? -1 : 1);
  const bfreAt = (src, m) => bfreRows.reduce((a, r) => a + bfreSign(r) * num((src[r.code] || r).months?.[String(m + 1)]), 0);
  const bfre = Array.from({ length: 12 }, (_, i) => bfreAt({}, i));
  const bfreOpen = bfreRows.reduce((a, r) => a + bfreSign(r) * prevDec(r.code), 0);
  const tresoBfre = treso.map((v, i) => v + bfre[i]);
  const tresoBfreOpen = tresoOpen + bfreOpen;

  // Séries de l'an passé (graphique 12 mois roulants).
  const prevTreso = hasPrev ? Array.from({ length: 12 }, (_, i) => sumRows(cashRows, prevByCode, i)) : null;
  const prevBfre = hasPrev ? Array.from({ length: 12 }, (_, i) => bfreAt(prevByCode, i)) : null;

  // ── 3 degrés de liquidité (table) ─────────────────────────────────────
  const acM = marr(findCode(bs.rows, 'sec:current_assets')?.months);
  const dctM = marr(findCode(bs.rows, 'sec:current_liabilities')?.months).map(Math.abs);
  const arClientsRow = rowByStd('ar_clients');
  const invStdRowL = rowByStd('inventory');
  const arBalM = marr(arClientsRow?.months);
  const invBalM = marr(invStdRowL?.months);
  const autresM = acM.map((a, i) => a - treso[i] - arBalM[i] - invBalM[i]);
  const d1num = treso;                                   // liquidités
  const d2num = treso.map((v, i) => v + arBalM[i]);      // + créances-clients (strict)
  const d3num = acM;                                     // actifs circulants
  const clip = (arr) => arr.map((v, i) => (i >= closed ? null : v));
  const valM = (nA) => Array.from({ length: 12 }, (_, m) => (m >= closed ? null : nA[m] - dctM[m]));
  const ratM = (nA) => Array.from({ length: 12 }, (_, m) => (m >= closed || !(dctM[m] > 0) ? null : nA[m] / dctM[m]));
  const acOpen = num(findCode(bsPrev?.rows, 'sec:current_assets')?.months?.['12']);
  const dctOpen = Math.abs(num(findCode(bsPrev?.rows, 'sec:current_liabilities')?.months?.['12']));
  const arOpen = prevDec(arClientsRow?.code);
  const invOpen = prevDec(invStdRowL?.code);
  const autresOpen = acOpen - tresoOpen - arOpen - invOpen;
  const oVal = (n0) => (hasPrev ? n0 - dctOpen : null);
  const oRat = (n0) => (hasPrev && dctOpen > 0 ? n0 / dctOpen : null);
  const detail = (label, vals, open) => ({ label, vals: clip(vals), open: hasPrev ? open : null, fmt: 'chf', cls: 'fwc-row--liqdetail' });
  const liqValues = [
    { label: tp('phrases:p_1b2c78120d'), vals: clip(treso), open: hasPrev ? tresoOpen : null, fmt: 'chf', cls: 'fwc-row--liqgross' },
    { label: tp('phrases:p_f4a4f19dc6'), vals: clip(dctM), open: hasPrev ? dctOpen : null, fmt: 'chf', cls: 'fwc-row--liqref' },
    { label: tp('phrases:p_865113c71e'), vals: valM(d1num), open: oVal(tresoOpen), fmt: 'chf', cls: 'fwc-row--liqval' },
    detail(p('+ Créances-clients'), arBalM, arOpen),
    { label: tp('phrases:p_6649e3787e'), vals: valM(d2num), open: oVal(tresoOpen + arOpen), fmt: 'chf', cls: 'fwc-row--liqval' },
    detail(p('+ Stocks'), invBalM, invOpen),
    detail(p('+ Autres actifs circulants'), autresM, autresOpen),
    { label: p('Degré 3 — Liquidité générale = FRN'), vals: valM(d3num), open: oVal(acOpen), fmt: 'chf', cls: 'fwc-row--liqfrn' },
  ];
  const liqRatios = [
    { label: tp('phrases:p_865113c71e'), sub: p('Cash ratio · liquidités ÷ DCT'), cible: '≥ 0.2–0.3', vals: ratM(d1num), open: oRat(tresoOpen), fmt: 'ratio', cls: 'fwc-row--liqval' },
    { label: tp('phrases:p_6649e3787e'), sub: p('Quick ratio · (liquidités + créances) ÷ DCT'), cible: '≥ 1.0', vals: ratM(d2num), open: oRat(tresoOpen + arOpen), fmt: 'ratio', cls: 'fwc-row--liqval' },
    { label: tp('phrases:p_19537d50ff'), sub: p('Current ratio · actifs circulants ÷ DCT'), cible: '1.5–2.0', vals: ratM(d3num), open: oRat(acOpen), fmt: 'ratio', cls: 'fwc-row--liqfrn' },
  ];

  const li = closed - 1;

  // ── Graphique 12 mois roulants jusqu'au dernier mois clôturé ────────────
  const apRowL = rowByStd('ap_suppliers');
  const creancesN = marr(arClientsRow?.months);
  const prevCreances = hasPrev ? marr(prevByCode[arClientsRow?.code]?.months) : null;
  const stockN = marr(invStdRowL?.months);
  const prevStock = hasPrev ? marr(prevByCode[invStdRowL?.code]?.months) : null;
  const fournN = marr(apRowL?.months).map((v) => -Math.abs(v));
  const prevFourn = hasPrev ? marr(prevByCode[apRowL?.code]?.months).map((v) => -Math.abs(v)) : null;
  const persoN = pl ? marr(findCode(pl.rows, 'sec:personnel')?.months).map(Math.abs) : null;
  const prevPerso = plPrev ? marr(findCode(plPrev.rows, 'sec:personnel')?.months).map(Math.abs) : null;
  const at = (cur, prev, a) => (a >= 0 ? cur[a] : (prev ? prev[a + 12] : null));
  const rolling = Array.from({ length: 12 }, (_, k) => {
    const a = (closed - 1) - 11 + k;            // index absolu (négatif = an passé)
    const yr = a >= 0 ? year : year - 1;
    const mm = ((a % 12) + 12) % 12;
    const t = at(treso, prevTreso, a);
    const cr = at(creancesN, prevCreances, a);
    const st = at(stockN, prevStock, a);
    const fo = at(fournN, prevFourn, a);
    const pe = persoN ? at(persoN, prevPerso, a) : null;
    const net = (t == null || cr == null || fo == null) ? null : Math.round(t + cr + (st || 0) + fo);
    return {
      m: `${M3[mm]} ${String(yr).slice(2)}`,
      treso: t == null ? null : Math.round(t),
      creances: cr == null ? null : Math.round(cr),
      stock: st == null ? null : Math.round(st),
      fourn: fo == null ? null : Math.round(fo),
      perso: pe == null ? null : Math.round(pe),
      net,
    };
  });

  // Trésorerie actuelle + tendance (tuile Synthèse).
  const tresoNow = treso[li];
  const tresoPrevM = li > 0 ? treso[li - 1] : (hasPrev ? tresoOpen : null);
  const tresoPrev12 = at(treso, prevTreso, li - 12);
  const tresoTrend = {
    now: tresoNow,
    mom: tresoPrevM == null ? null : tresoNow - tresoPrevM,
    yoy: tresoPrev12 == null ? null : tresoNow - tresoPrev12,
  };

  // ── Onglet BFRE : tables 12 mois roulants (passés) ─────────────────────
  const arStdRow = rowByStd('ar_clients');
  const invStdRow = rowByStd('inventory');
  const apStdRow = rowByStd('ap_suppliers');

  const balAt = (r, a) => {
    if (!r) return 0;
    if (a >= 0) return num(r.months?.[String(a + 1)]);
    return num(prevByCode[r.code]?.months?.[String(a + 13)]);
  };
  const balAtN = (r, a) => {
    if (!r) return 0;
    if (a >= 0) return num(r.months?.[String(a + 1)]);
    if (a >= -12) return hasPrev ? num(prevByCode[r.code]?.months?.[String(a + 13)]) : null;
    if (a >= -24) return hasPrev2 ? num(prev2ByCode[r.code]?.months?.[String(a + 25)]) : null;
    return null;
  };
  const avgBal = (r, a) => {
    let s = 0;
    for (let kk = a - 11; kk <= a; kk++) {
      const v = balAtN(r, kk);
      if (v == null) return null;
      s += v;
    }
    return s / 12;
  };
  const flowAt = (Y, P, P2, a) => {
    if (a >= 0) return Y ? Y[a] : null;
    if (a >= -12) return P ? P[a + 12] : null;
    if (a >= -24) return P2 ? P2[a + 24] : null;
    return null;
  };
  const roll12 = (Y, P, P2, a) => {
    let s = 0;
    for (let kk = a - 11; kk <= a; kk++) {
      const v = flowAt(Y, P, P2, kk);
      if (v == null) return null;
      s += v;
    }
    return s;
  };

  const plArr = (plv, code) => (plv ? marr(findCode(plv.rows, `sec:${code}`)?.months) : null);
  const revY = plArr(pl, 'revenue'), revP = plArr(plPrev, 'revenue'), revP2 = plArr(plPrev2, 'revenue');
  const cogsY = plArr(pl, 'cogs'), cogsP = plArr(plPrev, 'cogs'), cogsP2 = plArr(plPrev2, 'cogs');
  const opexY = plArr(pl, 'other_opex'), opexP = plArr(plPrev, 'other_opex'), opexP2 = plArr(plPrev2, 'other_opex');
  const absC = (a) => (a == null ? null : Math.abs(a));

  const last = closed - 1;                         // dernier mois clôturé (index N)
  const winA = Array.from({ length: 12 }, (_, kk) => (last - 11) + kk);   // fenêtre roulante
  const rollLabels = winA.map((a) => {
    const yr = a >= 0 ? year : year - 1;
    const mm = ((a % 12) + 12) % 12;
    return `${M3[mm]} ${String(yr).slice(2)}`;
  });

  const rollComps = bfreRows.map((r) => ({
    label: r.label,
    vals: winA.map((a) => bfreSign(r) * balAt(r, a)),
  }));
  const rollBfre = winA.map((a) => bfreRows.reduce((acc, r) => acc + bfreSign(r) * balAt(r, a), 0));
  const rollTreso = winA.map((a) => cashRows.reduce((acc, r) => acc + balAt(r, a), 0));
  const rollBfreTreso = rollBfre.map((v, i) => v + rollTreso[i]);

  const vatRate = Number(activeTenant?.vat_rate);
  const vatMult = Number.isFinite(vatRate) ? 1 + vatRate : null;
  const absAvg = (r, a) => { const v = avgBal(r, a); return v == null ? null : Math.abs(v); };
  const arRoll = winA.map((a) => absAvg(arStdRow, a));    // créances moyennes 12 mois
  const invRoll = winA.map((a) => absAvg(invStdRow, a));  // stocks moyens 12 mois
  const apRoll = winA.map((a) => absAvg(apStdRow, a));    // dettes fourn. moyennes 12 mois
  const canRoll = winA.map((a) => roll12(revY, revP, revP2, a));
  const cogsRoll = winA.map((a) => absC(roll12(cogsY, cogsP, cogsP2, a)));
  const purchRoll = winA.map((a) => {
    const c = roll12(cogsY, cogsP, cogsP2, a);
    const o = roll12(opexY, opexP, opexP2, a);
    return (c == null || o == null) ? null : Math.abs(c) + Math.abs(o);
  });
  const daysDiv = (numr, den, mult) => (numr != null && den && den > 0 && mult ? (numr / (den * mult)) * 365 : null);
  const dsoRoll = winA.map((_, i) => daysDiv(arRoll[i], canRoll[i], vatMult));
  const dioRoll = winA.map((_, i) => daysDiv(invRoll[i], purchRoll[i], 1));
  const dpoRoll = winA.map((_, i) => daysDiv(apRoll[i], cogsRoll[i], vatMult));
  // Acomptes clients (encaissés d'avance) : raccourcissent le cycle client →
  // délai NÉGATIF, même dénominateur que le DSO (CA net, TTC).
  const advRow = rowByStd('customer_advances');
  const advRoll = winA.map((a) => absAvg(advRow, a));
  const advDaysRoll = winA.map((_, i) => {
    const d = daysDiv(advRoll[i], canRoll[i], vatMult);
    return d == null ? null : -d;
  });

  const turns = (daysArr) => daysArr.map((d) => (d && d > 0 ? 365 / d : null));
  const bfreReport = {
    labels: rollLabels,
    comps: rollComps, bfre: rollBfre, treso: rollTreso, bfreTreso: rollBfreTreso,
    rotations: [
      // `details` = les séries qui entrent dans le calcul du délai (numérateur
      // moyen 12 mois, dénominateur flux 12 mois glissants) — dépliées au clic
      // sur le titre du bloc dans l'onglet BFRE.
      { key: 'dso', flowLabel: p('Chiffre d’affaires net (12 mois glissants)'), flowShort: p('Chiffre d’affaires net'), flow: canRoll,
        daysLabel: p('DSO, recouvrement client'), days: dsoRoll, turns: turns(dsoRoll), ttc: true,
        acompteDays: advDaysRoll,
        details: [
          { label: p('Créances-clients moyennes (12 mois)'), vals: arRoll },
          { label: p('Acomptes clients moyens (12 mois)'), vals: advRoll },
          { label: p('Chiffre d’affaires net (12 mois glissants)'), vals: canRoll },
        ],
        formula: p('Délai = Créances-clients moyennes (TTC, moyenne 12 mois) ÷ (CA net × (1 + TVA)) × 365   ·   Rotation = 365 ÷ Délai') },
      { key: 'dio', flowLabel: p('Achats — coûts + autres charges (12 mois glissants)'), flowShort: p('Achats — coûts + autres charges'), flow: purchRoll,
        daysLabel: p('DIO, écoulement stock'), days: dioRoll, turns: turns(dioRoll), ttc: false,
        details: [
          { label: p('Stocks moyens (12 mois)'), vals: invRoll },
          { label: p('Achats — coûts + autres charges (12 mois glissants)'), vals: purchRoll },
        ],
        formula: p('Délai = Stocks moyens (moyenne 12 mois) ÷ Achats × 365   ·   Rotation = 365 ÷ Délai') },
      { key: 'dpo', flowLabel: p('Coût des marchandises vendues (12 mois glissants)'), flowShort: p('Coût des marchandises vendues'), flow: cogsRoll,
        daysLabel: p('DPO, paiement fournisseurs'), days: dpoRoll, turns: turns(dpoRoll), ttc: true,
        details: [
          { label: p('Dettes fournisseurs moyennes (12 mois)'), vals: apRoll },
          { label: p('Coût des marchandises vendues (12 mois glissants)'), vals: cogsRoll },
        ],
        formula: p('Délai = Dettes fournisseurs moyennes (TTC, moyenne 12 mois) ÷ (Coût des marchandises × (1 + TVA)) × 365   ·   Rotation = 365 ÷ Délai') },
    ],
  };

  return {
    closed, treso, tresoOpen, components, componentsAll, bfre, bfreOpen, tresoBfre, tresoBfreOpen,
    hasPrev, hasBfre, liqValues, liqRatios, rolling, hasPl: !!pl, bfreReport, vatMult, tresoTrend,
    prevBfre,
  };
}
